import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Textarea } from "./components/ui/textarea";
import { Input } from "./components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { ScrollArea } from "./components/ui/scroll-area";
import { Separator } from "./components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Switch } from "./components/ui/switch";
import { AlertCircle, Brain, Zap, Download, History, User, CreditCard, LogOut, LogIn, UserPlus, Crown, Bot } from "lucide-react";
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { PaymentPage } from "./components/PaymentPage";
import { PricingBanner } from "./components/PricingBanner";
import { PasswordReset } from "./components/PasswordReset";
import { FileUpload } from "./components/FileUpload";
import { AccountDashboard } from "./components/AccountDashboard";
import { SupportChat } from "./components/SupportChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [problemText, setProblemText] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("lycée");
  const [detailedSolution, setDetailedSolution] = useState(true);
  const [solution, setSolution] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState("");
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [userProblems, setUserProblems] = useState([]);
  const [pricingPackages, setPricingPackages] = useState({});
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaymentPage, setShowPaymentPage] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showAccountDashboard, setShowAccountDashboard] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);

  // Auth form state
  const [authForm, setAuthForm] = useState({
    email: "",
    password: "",
    full_name: "",
    age: "",
    current_level: "lycée",
    target_level: "lycee_general",
    goal_note: ""
  });

  const levels = [
    { value: "collège", label: "Collège (6ème-3ème)" },
    { value: "lycée", label: "Lycée (2nde-Terminale)" },
    { value: "université", label: "Université (L1-M2)" },
    { value: "recherche", label: "Recherche (Doctorat+)" }
  ];

  const targetLevels = [
    { value: "lycee_general", label: "Lycée général" },
    { value: "lycee_professionnel", label: "Lycée Professionnel" },
    { value: "formation_distance", label: "Formation à distance" },
    { value: "bts", label: "BTS" },
    { value: "but", label: "BUT" },
    { value: "cpes", label: "CPES" },
    { value: "cpge", label: "CPGE" },
    { value: "deust", label: "DEUST" },
    { value: "dn_made", label: "DN MADE" },
    { value: "licence_1", label: "Licence 1" },
    { value: "licence_2", label: "Licence 2" },
    { value: "licence_pro", label: "Licence Pro." },
    { value: "licence_cupge", label: "Licence CUPGE" },
    { value: "master_1", label: "Master 1" },
    { value: "master_2", label: "Master 2" },
    { value: "ecole_specialisee", label: "École Spécialisée" },
    { value: "cmi", label: "CMI" },
    { value: "doctorat", label: "Doctorat" }
  ];

  useEffect(() => {
    if (token) {
      fetchUserInfo();
      loadUserProblems();
    }
    loadPricingPackages();
    checkPaymentReturn();
  }, [token]);

  // Debug useEffect for payment dialog state
  useEffect(() => {
    console.log("🔍 Payment dialog state changed to:", showPaymentDialog);
    if (showPaymentDialog) {
      console.log("🎉 PAYMENT DIALOG IS NOW OPEN!");
    }
  }, [showPaymentDialog]);
  
  // Force payment dialog visibility when credits are 0
  useEffect(() => {
    if (user && user.free_credits === 0 && !user.is_premium) {
      console.log("🔥 User has 0 credits and no premium - should show payment options");
    }
  }, [user]);

  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && token) {
      // Show payment processing message
      setError("");
      // Add loading indicator for payment verification
      setIsLoading(true);
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 6; // Increased attempts for better reliability
    
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.payment_status === 'paid') {
        // Payment successful
        await fetchUserInfo(); // Refresh user info to get updated credits/premium
        setError(""); // Clear any errors
        setIsLoading(false);
        setShowPaymentDialog(false); // Close payment dialog if open
        
        // Show success message briefly
        setError("✅ Paiement confirmé ! Vos crédits ont été ajoutés.");
        setTimeout(() => setError(""), 3000);
        
        // Remove session_id from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        // Payment expired
        setError("⏰ Session de paiement expirée. Veuillez réessayer.");
        setIsLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Continue polling if payment is still processing
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        // Max attempts reached
        setError("⏳ Vérification du paiement en cours... Veuillez patienter ou vérifier votre email.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setIsLoading(false);
      if (error.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        handleLogout();
      } else if (attempts >= maxAttempts) {
        setError("Erreur lors de la vérification du paiement. Contactez le support si le problème persiste.");
      } else {
        // Retry on network errors
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
      }
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching user info:", error);
      handleLogout();
    }
  };

  const loadUserProblems = async () => {
    try {
      const response = await axios.get(`${API}/my-problems?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProblems(response.data);
    } catch (error) {
      console.error("Error loading user problems:", error);
    }
  };

  const loadPricingPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPricingPackages(response.data.packages);
    } catch (error) {
      console.error("Error loading pricing packages:", error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      setError("");
      setResetMessage("");
      
      const response = await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail
      });
      
      setResetMessage("✅ " + response.data.message);
      setResetEmail("");
      
    } catch (error) {
      console.error("🚨 Forgot password error:", error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || "Erreur lors de la demande de réinitialisation";
      setError(errorMessage);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    
    // Validation for registration mode
    if (!isLoginMode) {
      if (!authForm.age || authForm.age < 10 || authForm.age > 99) {
        setError("Veuillez entrer un âge valide (10-99 ans)");
        return;
      }
      if (!authForm.current_level || !authForm.target_level) {
        setError("Veuillez sélectionner votre niveau actuel et votre formation visée");
        return;
      }
    }
    
    try {
      const endpoint = isLoginMode ? '/auth/login' : '/auth/register';
      
      // Prepare data - only send required fields for login
      const dataToSend = isLoginMode 
        ? { email: authForm.email, password: authForm.password }
        : {
            email: authForm.email,
            password: authForm.password,
            full_name: authForm.full_name,
            age: parseInt(authForm.age),
            current_level: authForm.current_level,
            target_level: authForm.target_level,
            goal_note: authForm.goal_note
          };
      
      console.log("🔍 Sending data:", dataToSend);
      const response = await axios.post(`${API}${endpoint}`, dataToSend);
      
      if (isLoginMode) {
        setToken(response.data.access_token);
        localStorage.setItem('token', response.data.access_token);
        setUser(response.data.user);
      } else {
        setUser(response.data);
        // Auto-login after registration
        const loginResponse = await axios.post(`${API}/auth/login`, {
          email: authForm.email,
          password: authForm.password
        });
        setToken(loginResponse.data.access_token);
        localStorage.setItem('token', loginResponse.data.access_token);
        setUser(loginResponse.data.user);
      }
      
      setShowAuthDialog(false);
      setAuthForm({ 
        email: "", 
        password: "", 
        full_name: "",
        age: "",
        current_level: "lycée",
        target_level: "lycee_general",
        goal_note: ""
      });
      setError("");
    } catch (error) {
      console.error("🚨 Auth error:", error.response?.data || error.message);
      
      // Gestion améliorée des erreurs de validation
      let errorMessage = "Erreur d'authentification";
      
      if (error.response?.status === 400) {
        // Erreur de validation 
        const errorData = error.response.data;
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Erreurs de validation Pydantic
            const validationErrors = errorData.detail.map(err => {
              const field = err.loc ? err.loc[err.loc.length - 1] : 'champ';
              return `${field}: ${err.msg}`;
            }).join(', ');
            errorMessage = `Validation: ${validationErrors}`;
          } else {
            errorMessage = errorData.detail;
          }
        } else {
          errorMessage = "Données invalides. Vérifiez tous les champs obligatoires.";
        }
      } else if (error.response?.status === 422) {
        errorMessage = "Format des données incorrect. Vérifiez vos informations.";
      } else if (error.response?.status === 401) {
        errorMessage = "Email ou mot de passe incorrect";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    setSolution(null);
    setUserProblems([]);
  };

  const handlePayment = async (packageId) => {
    try {
      setError(""); // Clear any existing errors
      const currentUrl = window.location.origin + window.location.pathname;
      
      const response = await axios.post(`${API}/checkout`, {
        package_id: packageId,
        success_url: `${currentUrl}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: currentUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error("URL de paiement non reçue");
      }
    } catch (error) {
      console.error("Payment error:", error);
      if (error.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        handleLogout();
      } else if (error.response?.status === 400) {
        setError("Package de paiement invalide. Veuillez réessayer.");
      } else {
        const errorMessage = error.response?.data?.detail || error.message || "Erreur lors du paiement";
        setError(typeof errorMessage === 'string' ? errorMessage : "Erreur lors de la création du paiement");
      }
    }
  };

  const solveProblem = async () => {
    if (!problemText.trim()) {
      setError("Veuillez saisir un problème mathématique");
      return;
    }

    if (!token) {
      setShowAuthDialog(true);
      return;
    }

    // Check credits for detailed solutions or non-college levels
    const requiresCredit = (selectedLevel !== "collège") || (selectedLevel === "collège" && detailedSolution);
    if (requiresCredit) {
      const now = new Date();
      const premiumExpired = !user?.premium_expires_at || new Date(user.premium_expires_at) <= now;
      
      if (user?.free_credits <= 0 && premiumExpired) {
        setError("Crédits insuffisants. Redirection vers la page de paiement...");
        setShowPaymentPage(true);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError("");
      setSolution(null);
      
      const response = await axios.post(`${API}/solve`, {
        problem_text: problemText,
        level: selectedLevel,
        detailed: detailedSolution
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSolution(response.data);
      
      // Refresh user info to update credits display
      await fetchUserInfo();
      await loadUserProblems();
      
      console.log("✅ Résolution terminée - Crédits mis à jour");
    } catch (error) {
      console.error("Erreur lors de la résolution:", error);
      console.log("Error response:", error.response); // Debug logging
      
      if (error.response?.status === 402) {
        // Handle payment required error - CRITICAL FIX
        console.log("🚨 402 PAYMENT REQUIRED ERROR DETECTED!");
        console.log("Current showPaymentDialog state:", showPaymentDialog);
        
        const errorData = error.response?.data?.detail;
        let errorMessage = "Crédits insuffisants. Veuillez acheter des crédits pour continuer.";
        
        if (typeof errorData === 'object' && errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
        
        // SOLUTION ALTERNATIVE: Redirect to payment page
        console.log("🚨 402 ERROR - Redirecting to payment page");
        setError(errorMessage);
        setShowPaymentPage(true);
        console.log("✅ Payment page will be displayed");
      } else if (error.response?.status === 401) {
        // Handle authentication error
        setError("Session expirée. Veuillez vous reconnecter.");
        handleLogout();
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.detail || error.message || "Erreur lors de la résolution du problème";
        setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderLatexContent = (text) => {
    if (!text) return null;
    
    let processedText = text;
    
    // Nettoyer les conversions déjà faites par le backend
    // Le backend a déjà converti \(...\) → $...$ et \[...\] → $$...$$
    
    // Split en préservant les délimiteurs
    // Pattern: capture $$...$$ (display) et $...$ (inline) séparément
    const parts = [];
    let currentIndex = 0;
    
    // Regex pour trouver les blocs $$...$$
    const displayRegex = /\$\$([\s\S]*?)\$\$/g;
    // Regex pour trouver inline $...$ (mais pas $$ qui est déjà traité)
    const inlineRegex = /(?<!\$)\$(?!\$)(.*?)(?<!\$)\$(?!\$)/g;
    
    let lastIndex = 0;
    let match;
    
    // Process display math first
    const displayMatches = [...processedText.matchAll(displayRegex)];
    const inlineMatches = [...processedText.matchAll(inlineRegex)];
    
    // Combine and sort all matches by position
    const allMatches = [
      ...displayMatches.map(m => ({type: 'display', match: m})),
      ...inlineMatches.map(m => ({type: 'inline', match: m}))
    ].sort((a, b) => a.match.index - b.match.index);
    
    allMatches.forEach((item, idx) => {
      const match = item.match;
      // Add text before match
      if (match.index > lastIndex) {
        parts.push({type: 'text', content: processedText.slice(lastIndex, match.index), key: `text-${idx}`});
      }
      
      // Add math
      if (item.type === 'display') {
        parts.push({type: 'display', content: match[1], key: `display-${idx}`});
      } else {
        parts.push({type: 'inline', content: match[1], key: `inline-${idx}`});
      }
      
      lastIndex = match.index + match[0].length;
    });
    
    // Add remaining text
    if (lastIndex < processedText.length) {
      parts.push({type: 'text', content: processedText.slice(lastIndex), key: 'text-end'});
    }
    
    return parts.map((part, index) => {
      try {
        if (part.type === 'display') {
          return <BlockMath key={part.key} math={part.content} />;
        } else if (part.type === 'inline') {
          return <InlineMath key={part.key} math={part.content} />;
        }
        return <span key={part.key}>{part.content}</span>;
      } catch (error) {
        console.error('Error rendering LaTeX:', error, part);
        return <span key={part.key} className="text-red-400">[Formule LaTeX invalide]</span>;
      }
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR');
  };

  const isPremium = user?.premium_expires_at && new Date(user.premium_expires_at) > new Date();

  // Check for password reset page
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const isResetPage = window.location.pathname.includes('reset-password') || resetToken;
  
  if (isResetPage) {
    return <PasswordReset />;
  }

  // Show account dashboard when needed
  if (showAccountDashboard) {
    return (
      <AccountDashboard
        user={user}
        token={token}
        onUserUpdate={async (updatedUser) => {
          setUser(updatedUser);
          await fetchUserInfo(); // Refresh user info
        }}
        onBack={() => setShowAccountDashboard(false)}
      />
    );
  }

  // Show payment page when needed
  if (showPaymentPage) {
    return (
      <PaymentPage 
        user={user}
        token={token}
        onBack={() => setShowPaymentPage(false)}
        onPaymentSuccess={async () => {
          setShowPaymentPage(false);
          await fetchUserInfo(); // Refresh user credits
          setError("✅ Paiement confirmé ! Vous pouvez maintenant continuer à résoudre des problèmes.");
          setTimeout(() => setError(""), 3000);
        }}
      />
    );
  }

  return (
    <div className="App">
      {/* Matrix-themed background */}
      <div className="matrix-bg">
        <div className="matrix-code"></div>
      </div>
      
      <div className="min-h-screen relative z-10">
        {/* Header */}
        <header className="border-b border-green-800/20 bg-black/80 backdrop-blur-md">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-green-400 font-mono">e = Math Skill</h1>
                  <p className="text-green-300/70 text-sm">Résolution Mathématique Avancée</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {user ? (
                  <>
                    <div className="flex items-center space-x-2">
                      {isPremium && <Crown className="w-4 h-4 text-yellow-400" />}
                      {/* Badge Crédits Éclair animé */}
                      <Badge 
                        variant="outline" 
                        className="border-yellow-500 text-yellow-400 bg-yellow-950/50 animate-pulse"
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        {user.lightning_credits || 0} ⚡
                      </Badge>
                      <Badge variant="outline" className="border-green-500 text-green-400 bg-green-950/50">
                        {user.free_credits} crédits
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSupportChat(true)}
                      className="border-blue-700 text-blue-300 hover:bg-blue-950"
                    >
                      <Bot className="w-4 h-4 mr-1" />
                      Support
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAccountDashboard(true)}
                      className="border-green-700 text-green-300 hover:bg-green-950"
                    >
                      <User className="w-4 h-4 mr-1" />
                      Mon Compte
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogout}
                      className="border-green-700 text-green-300 hover:bg-green-950"
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Déconnexion
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => setShowAuthDialog(true)}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black font-semibold"
                  >
                    <LogIn className="w-4 h-4 mr-1" />
                    Se connecter
                  </Button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Pricing Banner */}
        <div className="container mx-auto px-4 pt-4">
          <PricingBanner />
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Main solving interface */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Input Section */}
              <Card className="bg-black/40 border-green-800/30 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="text-green-400 flex items-center space-x-2">
                    <Brain className="w-5 h-5" />
                    <span>Énoncé du Problème</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-green-300 text-sm">Niveau d'étude:</label>
                    <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                      <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100 focus:ring-2 focus:ring-green-500 focus:border-green-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-green-700 z-50">
                        {levels.map(level => (
                          <SelectItem 
                            key={level.value} 
                            value={level.value} 
                            className="text-green-100 hover:bg-green-950 focus:bg-green-950 cursor-pointer"
                          >
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Detailed solution toggle */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <label className="text-green-300 text-sm">Résolution détaillée</label>
                      {selectedLevel === "collège" && !detailedSolution && (
                        <p className="text-green-400 text-xs">✓ Gratuit pour le niveau collège</p>
                      )}
                      {(selectedLevel !== "collège" || detailedSolution) && (
                        <p className="text-green-300 text-xs">
                          {user?.free_credits > 0 || isPremium ? "✓ Inclus" : "0,99€ requis"}
                        </p>
                      )}
                    </div>
                    <Switch
                      checked={detailedSolution}
                      onCheckedChange={setDetailedSolution}
                      className="data-[state=checked]:bg-green-600"
                    />
                  </div>
                  
                  {/* Mode Selector */}
                  <div className="space-y-2">
                    <div className="flex bg-black/40 border border-green-700/50 rounded-lg p-1">
                      <Button
                        type="button"
                        variant={!showFileUpload ? "default" : "ghost"}
                        onClick={() => setShowFileUpload(false)}
                        className={`flex-1 ${!showFileUpload 
                          ? 'bg-green-600 text-black' 
                          : 'text-green-300 hover:text-green-200'
                        }`}
                      >
                        ✏️ Saisie texte
                      </Button>
                      <Button
                        type="button"
                        variant={showFileUpload ? "default" : "ghost"}
                        onClick={() => setShowFileUpload(true)}
                        className={`flex-1 ${showFileUpload 
                          ? 'bg-green-600 text-black' 
                          : 'text-green-300 hover:text-green-200'
                        }`}
                      >
                        📎 Upload fichier
                      </Button>
                    </div>
                  </div>

                  {/* Content based on mode */}
                  {!showFileUpload ? (
                    <div className="space-y-2">
                      <label className="text-green-300 text-sm">Problème mathématique:</label>
                      <Textarea
                        value={problemText}
                        onChange={(e) => setProblemText(e.target.value)}
                        placeholder="Collez votre énoncé mathématique ici (équations, systèmes, intégrales, démonstrations...)

Exemples:
• Résoudre: 2x² - 5x + 3 = 0
• Calculer l'intégrale de x²dx de 0 à 2
• Démontrer que √2 est irrationnel
• Trouver la limite de (sin x)/x quand x tend vers 0"
                        className="min-h-[120px] bg-black/60 border-green-700/50 text-green-100 placeholder:text-green-500/50 resize-none font-mono"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FileUpload
                        user={user}
                        token={token}
                        selectedLevel={selectedLevel}
                        setSelectedLevel={setSelectedLevel}
                        onSolutionReceived={(solution) => {
                          // Ajouter la solution à l'historique et l'afficher
                          setSolution(solution.solution_text);
                          setProblemText(`Fichier uploadé: ${solution.file_info?.filename}\n\n${solution.problem_text}`);
                          setShowFileUpload(false); // Retourner au mode texte pour voir la solution
                          
                          // Recharger les données utilisateur pour mettre à jour les crédits
                          fetchUserInfo();
                        }}
                      />
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Button 
                      onClick={solveProblem}
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black font-semibold h-12 rounded-lg"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
                          Résolution en cours...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-2" />
                          Résoudre le Problème
                        </>
                      )}
                    </Button>
                    
                    {/* TEST: Button to access payment page */}
                    {user && (
                      <Button 
                        onClick={() => {
                          console.log("🧪 OPENING PAYMENT PAGE");
                          setShowPaymentPage(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full border-yellow-600 text-yellow-400 hover:bg-yellow-950"
                      >
                        💳 Voir les Forfaits
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Error/Success Display */}
              {error && (
                <Card className={`${
                  error.includes('✅') ? 'bg-green-950/20 border-green-800/30' : 'bg-red-950/20 border-red-800/30'
                }`}>
                  <CardContent className="py-4">
                    <div className={`flex items-center space-x-2 ${
                      error.includes('✅') ? 'text-green-400' : 'text-red-400'
                    }`}>
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Solution Display */}
              {solution && (
                <Card className="bg-black/40 border-green-800/30 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-green-400 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Brain className="w-5 h-5" />
                        <span>Solution</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="border-green-500 text-green-400 bg-green-950/50">
                          {solution.level}
                        </Badge>
                        {solution.is_detailed && (
                          <Badge variant="outline" className="border-pink-500 text-pink-400 bg-pink-950/50">
                            Détaillée
                          </Badge>
                        )}
                        <Button size="sm" variant="outline" className="border-green-700 text-green-300 hover:bg-green-950">
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="solution" className="w-full">
                      <TabsList className="bg-black/60 border-green-700">
                        <TabsTrigger value="solution" className="data-[state=active]:bg-green-950 data-[state=active]:text-green-400">
                          Solution Complète
                        </TabsTrigger>
                        {solution.is_detailed && solution.step_by_step?.length > 0 && (
                          <TabsTrigger value="steps" className="data-[state=active]:bg-green-950 data-[state=active]:text-green-400">
                            Étapes Détaillées
                          </TabsTrigger>
                        )}
                        {solution.latex_formulas?.length > 0 && (
                          <TabsTrigger value="formulas" className="data-[state=active]:bg-green-950 data-[state=active]:text-green-400">
                            Formules LaTeX
                          </TabsTrigger>
                        )}
                      </TabsList>
                      
                      <TabsContent value="solution" className="mt-4">
                        <ScrollArea className="h-[600px] pr-4">
                          <div className="prose prose-invert prose-green max-w-none">
                            <div className="text-green-100 leading-relaxed whitespace-pre-wrap text-base" style={{fontFamily: "'Inter', sans-serif", lineHeight: '1.9'}}>
                              {renderLatexContent(solution.solution_text)}
                            </div>
                          </div>
                        </ScrollArea>
                      </TabsContent>
                      
                      {solution.is_detailed && solution.step_by_step?.length > 0 && (
                        <TabsContent value="steps" className="mt-4">
                          <ScrollArea className="h-[600px] pr-4">
                            <div className="space-y-4">
                              {solution.step_by_step.map((step, index) => (
                                <div key={index} className="flex space-x-4">
                                  <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-black rounded-full flex items-center justify-center font-semibold text-sm">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 text-green-100 leading-relaxed">
                                    {renderLatexContent(step)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </TabsContent>
                      )}
                      
                      {solution.latex_formulas?.length > 0 && (
                        <TabsContent value="formulas" className="mt-4">
                          <ScrollArea className="h-[600px] pr-4">
                            <div className="space-y-4">
                              {solution.latex_formulas.map((formula, index) => (
                                <div key={index} className="bg-black/60 rounded-lg p-4 border border-green-800/30">
                                  <BlockMath math={formula} />
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </TabsContent>
                      )}
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* User Problems */}
              {user && (
                <Card className="bg-black/40 border-green-800/30 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-green-400 flex items-center space-x-2">
                      <History className="w-5 h-5" />
                      <span>Mes Problèmes</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {userProblems.map((problem, index) => (
                          <div key={problem.id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline" className="border-green-600 text-green-400 bg-green-950/30 text-xs">
                                {problem.level}
                              </Badge>
                              <span className="text-xs text-green-500">
                                {formatTimestamp(problem.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm text-green-200 line-clamp-2 leading-relaxed">
                              {problem.problem_text}
                            </p>
                            {index < userProblems.length - 1 && (
                              <Separator className="bg-green-800/30" />
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Features Info */}
              <Card className="bg-black/40 border-green-800/30 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="text-green-400 text-lg">Fonctionnalités</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                      <div>
                        <p className="text-green-300 font-medium">Résolution Multi-Niveaux</p>
                        <p className="text-green-300">Du collège à la recherche avancée</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                      <div>
                        <p className="text-green-300 font-medium">Support LaTeX</p>
                        <p className="text-green-300">Formules mathématiques formatées</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                      <div>
                        <p className="text-green-300 font-medium">IA Avancée</p>
                        <p className="text-green-300">GPT-5 spécialisé en mathématiques</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                      <div>
                        <p className="text-green-300 font-medium">Explications Détaillées</p>
                        <p className="text-green-300">Méthodes adaptées au niveau</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="bg-black/90 border-green-800/30 text-green-100">
          <DialogHeader>
            <DialogTitle className="text-green-400">
              {isLoginMode ? 'Connexion' : 'Inscription'}
            </DialogTitle>
            <DialogDescription className="text-green-300">
              {isLoginMode ? 'Connectez-vous pour accéder à vos problèmes' : 'Créez votre compte pour commencer'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
              className="bg-black/60 border-green-700/50 text-green-100"
              required
            />
            <Input
              type="password"
              placeholder="Mot de passe"
              value={authForm.password}
              onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
              className="bg-black/60 border-green-700/50 text-green-100"
              required
            />
            
            {/* Forgot Password Form */}
            {showForgotPassword && (
              <div className="space-y-4 p-4 bg-green-950/20 border border-green-800/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-green-400 font-medium">Réinitialiser le mot de passe</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail("");
                      setResetMessage("");
                      setError("");
                    }}
                    className="text-green-300 hover:text-green-100 p-1"
                  >
                    ✕
                  </Button>
                </div>
                <Input
                  type="email"
                  placeholder="Votre adresse email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="bg-black/60 border-green-700/50 text-green-100"
                  required
                />
                {resetMessage && (
                  <div className="text-green-400 text-sm">{resetMessage}</div>
                )}
                <Button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold"
                >
                  Envoyer le lien de réinitialisation
                </Button>
              </div>
            )}
            
            {!isLoginMode && (
              <>
                <Input
                  type="text"
                  placeholder="Nom complet"
                  value={authForm.full_name}
                  onChange={(e) => setAuthForm({...authForm, full_name: e.target.value})}
                  className="bg-black/60 border-green-700/50 text-green-100"
                  required
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-green-300 text-xs">Âge</label>
                    <Input
                      type="number"
                      placeholder="Votre âge"
                      min="10"
                      max="99"
                      value={authForm.age}
                      onChange={(e) => setAuthForm({...authForm, age: e.target.value})}
                      className="bg-black/60 border-green-700/50 text-green-100"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-green-300 text-xs">Niveau actuel</label>
                    <Select 
                      value={authForm.current_level} 
                      onValueChange={(value) => setAuthForm({...authForm, current_level: value})}
                    >
                      <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-green-700">
                        {levels.map(level => (
                          <SelectItem 
                            key={level.value} 
                            value={level.value} 
                            className="text-green-100 hover:bg-green-950"
                          >
                            {level.label.split(' ')[0]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-green-300 text-xs">Formation/Niveau visé</label>
                  <Select 
                    value={authForm.target_level} 
                    onValueChange={(value) => setAuthForm({...authForm, target_level: value})}
                  >
                    <SelectTrigger className="bg-black/60 border-green-700/50 text-green-100">
                      <SelectValue placeholder="Choisir votre objectif" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-green-700 max-h-60 overflow-y-auto">
                      {targetLevels.map(level => (
                        <SelectItem 
                          key={level.value} 
                          value={level.value} 
                          className="text-green-100 hover:bg-green-950 cursor-pointer"
                        >
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-green-300 text-xs">But recherché (optionnel)</label>
                  <Input
                    value={authForm.goal_note}
                    onChange={(e) => setAuthForm({...authForm, goal_note: e.target.value})}
                    placeholder="Ex: Intégrer une école d'ingénieur, réussir le bac..."
                    className="bg-black/60 border-green-700/50 text-green-100 placeholder-green-500/50"
                  />
                </div>

                {/* Pricing preview based on selected level */}
                {authForm.current_level && authForm.target_level && (
                  <div className="bg-green-950/20 border border-green-800/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm font-medium mb-1">
                      💰 Tarifs pour votre niveau ({levels.find(l => l.value === authForm.current_level)?.label})
                    </p>
                    <p className="text-green-300 text-xs mb-2">
                      🎯 Objectif : {targetLevels.find(l => l.value === authForm.target_level)?.label}
                    </p>
                    <div className="space-y-1 text-xs text-green-300">
                      {authForm.current_level === "collège" ? (
                        <>
                          <p>• Résolutions simples : <span className="text-green-400 font-semibold">GRATUIT</span></p>
                          <p>• Résolutions détaillées : <span className="text-green-400 font-semibold">0,99€/problème</span></p>
                          <p>• Accès journalier : <span className="text-green-400 font-semibold">0,99€/jour</span></p>
                        </>
                      ) : authForm.current_level === "lycée" ? (
                        <>
                          <p>• Accès journalier : <span className="text-green-400 font-semibold">0,99€/jour</span></p>
                          <p>• Accès hebdomadaire : <span className="text-green-400 font-semibold">3,99€/semaine</span></p>
                          <p>• Accès mensuel : <span className="text-green-400 font-semibold">7,99€/mois</span></p>
                        </>
                      ) : (
                        <>
                          <p>• Accès journalier : <span className="text-green-400 font-semibold">0,99€/jour</span></p>
                          <p>• Accès hebdomadaire : <span className="text-green-400 font-semibold">3,99€/semaine</span></p>
                          <p>• Accès mensuel : <span className="text-green-400 font-semibold">11,99€/mois</span></p>
                        </>
                      )}
                      <p className="text-green-400 font-medium">+ 3 crédits gratuits à l'inscription ! 🎉</p>
                    </div>
                  </div>
                )}
              </>
            )}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black font-semibold"
            >
              {isLoginMode ? <LogIn className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              {isLoginMode ? 'Se connecter' : "S'inscrire"}
            </Button>
            
            {/* Forgot Password Link */}
            {isLoginMode && !showForgotPassword && (
              <Button
                type="button"
                variant="link"
                onClick={() => setShowForgotPassword(true)}
                className="text-green-400 hover:text-green-300 text-sm p-0 h-auto"
              >
                Mot de passe oublié ?
              </Button>
            )}
            
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsLoginMode(!isLoginMode)}
              className="w-full text-green-300 hover:text-green-100"
            >
              {isLoginMode ? "Pas de compte ? S'inscrire" : "Déjà un compte ? Se connecter"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog - CRITICAL COMPONENT */}
      <Dialog 
        open={showPaymentDialog} 
        onOpenChange={(open) => {
          console.log("Payment dialog onOpenChange:", open);
          setShowPaymentDialog(open);
        }}
      >
        <DialogContent className="bg-black/90 border-green-800/30 text-green-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-green-400 flex items-center space-x-2">
              <CreditCard className="w-5 h-5" />
              <span>Crédits épuisés - Choisissez votre formule</span>
            </DialogTitle>
            <DialogDescription className="text-green-300">
              Vous avez utilisé vos 3 crédits gratuits. Choisissez une formule pour continuer à résoudre des problèmes mathématiques.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {Object.entries(pricingPackages).filter(([id]) => id !== 'college_detailed').map(([id, pkg]) => (
              <div key={id} className="border border-green-800/30 rounded-lg p-4 hover:border-green-700/50 transition-colors">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h3 className="text-green-300 font-medium text-lg">{pkg.name}</h3>
                    <p className="text-green-300 text-sm mt-1">
                      {id === 'daily_unlimited' && '🕐 Résolutions illimitées pendant 24 heures'}
                      {id === 'weekly_unlimited' && '📅 Résolutions illimitées pendant 7 jours - POPULAIRE'}
                      {id === 'monthly_unlimited' && '🏆 Résolutions illimitées pendant 30 jours - MEILLEUR PRIX'}
                    </p>
                    {id === 'weekly_unlimited' && (
                      <Badge className="mt-2 bg-green-600 text-black">Recommandé</Badge>
                    )}
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span className="text-green-400 font-bold text-xl">{pkg.price.toFixed(2)}€</span>
                    <Button
                      onClick={() => {
                        console.log("Payment button clicked for:", id);
                        handlePayment(id);
                      }}
                      size="sm"
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-black font-semibold"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Acheter
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Special college detailed option */}
            {Object.entries(pricingPackages).filter(([id]) => id === 'college_detailed').map(([id, pkg]) => (
              <div key={id} className="border border-blue-800/30 rounded-lg p-4 bg-blue-950/10">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <h3 className="text-blue-300 font-medium">{pkg.name}</h3>
                    <p className="text-blue-400 text-sm">Une seule résolution détaillée pour le niveau collège</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-400 font-bold">{pkg.price.toFixed(2)}€</span>
                    <Button
                      onClick={() => handlePayment(id)}
                      size="sm"
                      variant="outline"
                      className="border-blue-600 text-blue-400 hover:bg-blue-950"
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      Acheter
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer avec email support */}
      <footer className="relative z-10 mt-16 py-6 border-t border-green-800/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <Brain className="w-5 h-5 text-green-400" />
              <span className="text-green-300 font-semibold">e = Math Skill</span>
              <span className="text-green-500/50">|</span>
              <span className="text-green-400 text-sm">Résolution mathématique avancée</span>
            </div>
            
            <div className="flex items-center space-x-6">
              <a 
                href="mailto:noreply@mathskill.academy" 
                className="flex items-center space-x-2 text-green-400 hover:text-green-300 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span className="text-sm">noreply@mathskill.academy</span>
              </a>
              
              <div className="text-green-500/50 text-sm">
                © 2025 Math Skill Academy
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Support Chat Modal */}
      {showSupportChat && user && (
        <SupportChat
          user={user}
          token={token}
          onClose={() => setShowSupportChat(false)}
        />
      )}
    </div>
  );
}

export default App;